import React, { CSSProperties, MouseEvent, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { createPortal } from 'react-dom';
import { Link, Path } from 'react-router-dom';
import { classNames } from 'primereact/utils';
import { knowledgeBaseApi } from 'api/knowledgeBase';
import { globalSearchApi, GlobalSearchKnowledgeBaseResult, GlobalSearchLocalResult } from 'api/globalSearch';
import { useAuth } from 'contexts/authContext';
import { useGlobal } from 'contexts/globalContext';
import { usePanel } from 'contexts/panelContext';
import { useTranslation } from 'react-i18next';
import { QuickLinksPairsSection } from 'components/wall/QuickLinks';
import { InputText } from 'components/_new/InputText';
import { Icon } from 'components/_new/Icon';
import { slugify } from 'utils/helpers';
import { Box, BoxSection } from 'components/_new/Box';
import './GlobalSearch.scss';

type GlobalSearchProps = {
	className?: string;
	isOpen?: boolean;
	onOpenChange?: (isOpen: boolean) => void;
	searchValue?: string;
	onSearchValueChange?: (value: string) => void;
};

type GlobalSearchSectionKey = 'navigation' | 'pairs' | 'timeline' | 'knowledgeBase';
type GlobalSearchResultItem = GlobalSearchLocalResult | GlobalSearchKnowledgeBaseResult;

const MIN_SEARCH_LENGTH = 3;
const MOBILE_BREAKPOINT = 768;
const DESKTOP_MIN_WIDTH = 500;
const DROPDOWN_GAP = 4;
const MOBILE_VIEWPORT_PADDING = 10;
const VIEWPORT_PADDING = 8;
const BACKDROP_Z_INDEX = 9998;
const DROPDOWN_Z_INDEX = 9999;
const INPUT_Z_INDEX = 10000;
const KNOWLEDGE_BASE_VISIBLE_RESULTS_LIMIT = 5;
const SEARCH_SECTIONS: GlobalSearchSectionKey[] = ['navigation', 'pairs', 'timeline', 'knowledgeBase'];
const isPositiveRealNumber = (value: string) => {
	const normalizedValue = value.replace(',', '.');

	if (!/^\d+(\.\d+)?$/.test(normalizedValue)) {
		return false;
	}

	return Number(normalizedValue) > 0;
};
const isSearchEligible = (value: string) => value.length >= MIN_SEARCH_LENGTH || isPositiveRealNumber(value);

const getResultItemClassName = (clickable = true, extraClassName?: string) =>
	classNames('global-search-result-item', extraClassName, {
		'global-search-result-item-clickable': clickable,
		'global-search-result-item-static': !clickable,
	});

const GlobalSearchResultContent = ({ item }: { item: GlobalSearchResultItem }) => (
	<div className="flex flex-column gap-1">
		<span className="text-900">{item.label}</span>
		{item.info && <span className="text-xs text-600">{item.info}</span>}
	</div>
);

type GlobalSearchInternalResultLinkProps = {
	item: GlobalSearchResultItem;
	route: Partial<Path>;
	onSelect: (item: GlobalSearchResultItem) => void;
	onOpenInNewTab: (item: GlobalSearchResultItem) => void;
};

const GlobalSearchInternalResultLink = ({
	item,
	route,
	onSelect,
	onOpenInNewTab,
}: GlobalSearchInternalResultLinkProps) => {
	const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
		if (event.metaKey || event.ctrlKey) {
			onOpenInNewTab(item);
			return;
		}

		onSelect(item);
	};

	const handleAuxClick = (event: MouseEvent<HTMLAnchorElement>) => {
		if (event.button === 1) {
			onOpenInNewTab(item);
		}
	};

	return (
		<Link
			to={route}
			className={getResultItemClassName(true, 'no-underline')}
			onClick={handleClick}
			onAuxClick={handleAuxClick}
		>
			<GlobalSearchResultContent item={item} />
		</Link>
	);
};

type GlobalSearchActionButtonProps = {
	item: GlobalSearchResultItem;
	onSelect: (item: GlobalSearchResultItem) => void;
};

const GlobalSearchActionButton = ({ item, onSelect }: GlobalSearchActionButtonProps) => (
	<button type="button" className={getResultItemClassName()} onClick={() => onSelect(item)}>
		<GlobalSearchResultContent item={item} />
	</button>
);

const GlobalSearchStaticResult = ({ item }: { item: GlobalSearchResultItem }) => (
	<div className={getResultItemClassName(false)}>
		<GlobalSearchResultContent item={item} />
	</div>
);

export const GlobalSearch = ({
	className,
	isOpen: controlledIsOpen,
	onOpenChange,
	searchValue: controlledSearchValue,
	onSearchValueChange,
}: GlobalSearchProps) => {
	const { t } = useTranslation();
	const { currentUser } = useAuth();

	const containerRef = useRef<HTMLDivElement>(null);
	const dropdownRef = useRef<HTMLDivElement>(null);
	const inputRef = useRef<HTMLInputElement>(null);
	const frameRef = useRef<number | null>(null);

	const { setMyAccountModalOpen, setMyCalendarModalOpen } = useGlobal();
	const { currentApplicationData, panelScopePath } = usePanel();

	const [uncontrolledIsOpen, setUncontrolledIsOpen] = useState(false);
	const [uncontrolledSearch, setUncontrolledSearch] = useState('');
	const [searchUuid, setSearchUuid] = useState<string | null>(null);
	const [debouncedSearch, setDebouncedSearch] = useState('');
	const [dropdownStyle, setDropdownStyle] = useState<CSSProperties | null>(null);
	const isOpen = controlledIsOpen ?? uncontrolledIsOpen;
	const search = controlledSearchValue ?? uncontrolledSearch;
	const trimmedSearch = search.trim();

	const setIsOpen = (value: boolean) => {
		if (controlledIsOpen === undefined) {
			setUncontrolledIsOpen(value);
		}

		onOpenChange?.(value);
	};

	const setSearch = (value: string) => {
		if (controlledSearchValue === undefined) {
			setUncontrolledSearch(value);
		}

		onSearchValueChange?.(value);
	};

	useEffect(() => {
		const timeout = window.setTimeout(() => {
			setDebouncedSearch(trimmedSearch);
		}, 300);

		return () => {
			window.clearTimeout(timeout);
		};
	}, [trimmedSearch]);

	useEffect(() => {
		if (!isOpen) {
			return undefined;
		}

		const applyDropdownPosition = () => {
			const anchorElement = containerRef.current;
			const rect = anchorElement?.getBoundingClientRect();
			if (!rect || rect.width === 0 || rect.height === 0 || !anchorElement) {
				setDropdownStyle(null);
				return;
			}

			const styles = window.getComputedStyle(anchorElement);
			if (styles.display === 'none' || styles.visibility === 'hidden') {
				setDropdownStyle(null);
				return;
			}

			if (window.innerWidth < MOBILE_BREAKPOINT) {
				setDropdownStyle({
					position: 'fixed',
					top: rect.bottom + DROPDOWN_GAP,
					left: MOBILE_VIEWPORT_PADDING,
					width: Math.max(window.innerWidth - MOBILE_VIEWPORT_PADDING * 2, 0),
					zIndex: DROPDOWN_Z_INDEX,
				});
				return;
			}

			const maxAllowedWidth = Math.max(window.innerWidth - VIEWPORT_PADDING * 2, 0);
			const width = Math.min(Math.max(rect.width, DESKTOP_MIN_WIDTH), maxAllowedWidth);
			const centeredLeft = rect.left + rect.width / 2 - width / 2;
			const maxLeft = window.innerWidth - VIEWPORT_PADDING - width;
			const left = Math.max(VIEWPORT_PADDING, Math.min(centeredLeft, maxLeft));

			setDropdownStyle({
				position: 'fixed',
				top: rect.bottom + DROPDOWN_GAP,
				left,
				width,
				zIndex: DROPDOWN_Z_INDEX,
			});
		};

		const updateDropdownPosition = () => {
			if (frameRef.current !== null) {
				window.cancelAnimationFrame(frameRef.current);
			}

			frameRef.current = window.requestAnimationFrame(() => {
				applyDropdownPosition();
				frameRef.current = null;
			});
		};

		updateDropdownPosition();

		const handleKeyDown = (event: KeyboardEvent) => {
			if (event.key === 'Escape') {
				closeSearch();
				inputRef.current?.blur();
			}
		};

		window.addEventListener('resize', updateDropdownPosition);
		window.addEventListener('scroll', updateDropdownPosition, true);
		document.addEventListener('keydown', handleKeyDown);

		return () => {
			if (frameRef.current !== null) {
				window.cancelAnimationFrame(frameRef.current);
				frameRef.current = null;
			}
			window.removeEventListener('resize', updateDropdownPosition);
			window.removeEventListener('scroll', updateDropdownPosition, true);
			document.removeEventListener('keydown', handleKeyDown);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isOpen]);

	const knowledgeBaseLinkPrefix = `${panelScopePath}/knowledge-base`;
	const shouldSearch = isSearchEligible(debouncedSearch);
	const isCurrentSearchEligible = isSearchEligible(trimmedSearch);

	const recommendedArticlesQuery = useQuery(
		['global-search-recommended', { applicationId: currentApplicationData.id }],
		() => knowledgeBaseApi.application.getRecommendedArticles(currentApplicationData.id),
		{
			enabled: isOpen && !shouldSearch,
		}
	);

	const searchResultsQuery = useQuery(
		['global-search-results', { applicationId: currentApplicationData.id, search: debouncedSearch }],
		() => globalSearchApi.search(currentApplicationData.id, debouncedSearch, searchUuid),
		{
			enabled: isOpen && shouldSearch,
			onSuccess: (data) => {
				if (!searchUuid && data.uuid) {
					setSearchUuid(data.uuid);
				}
			},
		}
	);

	const recommendedArticles = useMemo(
		() => recommendedArticlesQuery.data?.[0]?.articles || [],
		[recommendedArticlesQuery.data]
	);

	const closeSearch = () => {
		setIsOpen(false);
		setSearchUuid(null);
		setDropdownStyle(null);
	};

	const resetSearch = () => {
		closeSearch();
		setSearch('');
		setDebouncedSearch('');
	};

	useEffect(() => {
		if (!isCurrentSearchEligible) {
			if (debouncedSearch !== trimmedSearch) {
				setDebouncedSearch(trimmedSearch);
			}
		}
	}, [debouncedSearch, isCurrentSearchEligible, trimmedSearch]);

	useEffect(() => {
		if (!isCurrentSearchEligible && searchUuid !== null) {
			setSearchUuid(null);
		}
	}, [isCurrentSearchEligible, searchUuid]);

	useEffect(() => {
		if (searchUuid !== null) {
			setSearchUuid(null);
		}
	}, [currentUser?.language]);

	const getContextLabel = (context: string) => {
		const translationKey = `userPanel.globalSearch.contexts.${context}`;
		const label = t(translationKey);

		return label === translationKey ? context : label;
	};

	const resultSections = useMemo(
		() =>
			SEARCH_SECTIONS.map((section) => {
				const items = searchResultsQuery.data?.[section] || [];
				const hasMore = section === 'knowledgeBase' && items.length > KNOWLEDGE_BASE_VISIBLE_RESULTS_LIMIT;

				return {
					key: section,
					label: getContextLabel(section),
					items: hasMore ? items.slice(0, KNOWLEDGE_BASE_VISIBLE_RESULTS_LIMIT) : items,
					hasMore,
				};
			}),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[searchResultsQuery.data]
	);

	const handleAction = (item: GlobalSearchResultItem) => {
		if (!('action' in item) || !item.action) {
			return;
		}

		switch (item.action.name) {
			case 'OPEN_ACCOUNT_SETTINGS':
				setMyAccountModalOpen(true);
				resetSearch();
				break;
			case 'OPEN_MY_CALENDAR':
				setMyCalendarModalOpen(true);
				resetSearch();
				break;
			default:
				break;
		}
	};

	const registerCurrentSearchSelection = (item: GlobalSearchResultItem) => {
		const resultUuid = searchUuid || item.uuid || searchResultsQuery.data?.uuid;
		if (resultUuid) {
			globalSearchApi.registerSelection(resultUuid).catch(() => {
				// Tracking click should not block navigation.
			});
		}
	};

	const hasSearchResults = resultSections.some(({ items }) => items.length > 0);

	const handleResultLinkSelect = (item: GlobalSearchResultItem) => {
		registerCurrentSearchSelection(item);
		window.setTimeout(() => {
			resetSearch();
		}, 0);
	};

	const handleResultOpenInNewTab = (item: GlobalSearchResultItem) => {
		registerCurrentSearchSelection(item);
	};

	const handleResultActionSelect = (item: GlobalSearchResultItem) => {
		registerCurrentSearchSelection(item);
		if (item.action) {
			handleAction(item);
		}
	};

	const knowledgeBaseSearchRoute = useMemo(
		() => ({
			pathname: knowledgeBaseLinkPrefix,
			search: `?search=${encodeURIComponent(debouncedSearch)}`,
		}),
		[debouncedSearch, knowledgeBaseLinkPrefix]
	);

	const getSectionHeader = (section: (typeof resultSections)[number]) => {
		if (section.key !== 'knowledgeBase' || !section.hasMore) {
			return section.label;
		}

		return (
			<span className="global-search-section-header-with-action">
				<span>{section.label}</span>
				<Link
					to={knowledgeBaseSearchRoute}
					className="global-search-more-link no-underline text-xs font-medium text-primary"
					onClick={() => resetSearch()}
				>
					{t('userPanel.globalSearch.moreMaterials')}
				</Link>
			</span>
		);
	};

	return (
		<div
			ref={containerRef}
			className={classNames('relative w-full', className)}
			style={isOpen ? { zIndex: INPUT_Z_INDEX } : undefined}
		>
			<div className="relative w-full">
				<Icon
					name="search"
					className="absolute text-500"
					style={{
						left: '0.9rem',
						top: '50%',
						transform: 'translateY(-50%)',
						pointerEvents: 'none',
						zIndex: 1,
					}}
				/>
				<InputText
					ref={inputRef}
					search
					value={search}
					onFocus={() => setIsOpen(true)}
					onClick={() => setIsOpen(true)}
					onChange={(event) => {
						const nextSearch = event.currentTarget.value;
						const nextTrimmedSearch = nextSearch.trim();

						setSearch(nextSearch);
						if (!isSearchEligible(nextTrimmedSearch)) {
							setSearchUuid(null);
							setDebouncedSearch(nextTrimmedSearch);
						}
						if (!isOpen) {
							setIsOpen(true);
						}
					}}
					placeholder={t('userPanel.globalSearch.placeholder')}
					className="w-full"
					style={{ paddingLeft: '2.7rem' }}
					aria-label={t('userPanel.globalSearch.placeholder')}
				/>
			</div>

			{isOpen &&
				dropdownStyle &&
				createPortal(
					<>
						<div
							aria-hidden="true"
							onMouseDown={closeSearch}
							style={{
								position: 'fixed',
								inset: 0,
								background: 'rgba(0, 0, 0, 0.35)',
								zIndex: BACKDROP_Z_INDEX,
							}}
						/>
						<Box
							ref={dropdownRef}
							variant="white-bordered"
							dropShadow
							className="global-search-dropdown overflow-hidden"
							style={dropdownStyle}
						>
							{shouldSearch ? (
								<>
									{searchResultsQuery.isLoading ? (
										<BoxSection className="text-sm text-600">
											{t('userPanel.globalSearch.searching')}
										</BoxSection>
									) : null}

									{!searchResultsQuery.isLoading && !hasSearchResults ? (
										<BoxSection className="text-sm text-600">
											{t('userPanel.globalSearch.noResults')}
										</BoxSection>
									) : null}

									{resultSections.map(
										(section) =>
											section.items.length > 0 && (
												<BoxSection
													key={section.key}
													noPad
													header={getSectionHeader(section)}
													headerClassName="text-xs font-semibold text-500 uppercase tracking-1 pb-1"
												>
													<ul className="box-section-list box-section-list-standard">
														{section.items.map((item) => {
															if (item.route) {
																// const pathname = getResultPathname(item);
																// if (pathname && /^https?:\/\//.test(pathname)) {
																// 	return (
																// 		<GlobalSearchExternalResultLink
																// 			key={item.key}
																// 			item={item}
																// 			isLastItem={isLastItem}
																// 			href={`${pathname}${hash || ''}`}
																// 			onSelect={handleResultLinkSelect}
																// 		/>
																// 	);
																// }

																return (
																	<li
																		key={item.key}
																		className="box-section-list-item"
																	>
																		<GlobalSearchInternalResultLink
																			item={item}
																			route={item.route}
																			onSelect={handleResultLinkSelect}
																			onOpenInNewTab={handleResultOpenInNewTab}
																		/>
																	</li>
																);
															}

															if (item.action) {
																return (
																	<li
																		key={item.key}
																		className="box-section-list-item"
																	>
																		<GlobalSearchActionButton
																			item={item}
																			onSelect={handleResultActionSelect}
																		/>
																	</li>
																);
															}

															return (
																<li key={item.key} className="box-section-list-item">
																	<GlobalSearchStaticResult item={item} />
																</li>
															);
														})}
													</ul>
												</BoxSection>
											)
									)}
								</>
							) : (
								<>
									<BoxSection
										header={t('userPanel.globalSearch.quickLinks')}
										headerClassName="text-xs font-semibold text-500 uppercase tracking-1 pb-1"
									>
										<QuickLinksPairsSection onNavigate={closeSearch} />
									</BoxSection>

									<BoxSection
										noPad={
											recommendedArticlesQuery.isLoading ||
											(!recommendedArticlesQuery.isLoading && recommendedArticles.length === 0)
										}
										header={t('userPanel.globalSearch.knowledgeBase')}
										headerClassName="text-xs font-semibold text-500 uppercase tracking-1 pb-1"
									>
										{recommendedArticlesQuery.isLoading ? (
											<div className="text-sm text-600">
												{t('userPanel.globalSearch.loadingRecommendations')}
											</div>
										) : null}

										{!recommendedArticlesQuery.isLoading && recommendedArticles.length === 0 ? (
											<div className="text-sm text-600">
												{t('userPanel.globalSearch.noRecommendations')}
											</div>
										) : null}

										<ul className="box-section-list box-section-list-standard">
											{recommendedArticles.map((article) => (
												<li key={article.id} className="box-section-list-item">
													<Link
														to={`${knowledgeBaseLinkPrefix}/article/${article.articleId}${
															article.slug ? `-${slugify(article.slug)}` : ''
														}`}
														className={getResultItemClassName(true, 'no-underline')}
														onClick={() => resetSearch()}
													>
														<span className="text-900">{article.title}</span>
													</Link>
												</li>
											))}
										</ul>
									</BoxSection>

									{trimmedSearch.length > 0 &&
									trimmedSearch.length < MIN_SEARCH_LENGTH &&
									!isPositiveRealNumber(trimmedSearch) ? (
										<BoxSection className="text-xs text-500">
											{t('userPanel.globalSearch.minCharacters')}
										</BoxSection>
									) : null}
								</>
							)}
						</Box>
					</>,
					document.body
				)}
		</div>
	);
};
